import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { FramerTooltip } from "@ryde/controls";
import { ViewSwitchWrapper } from "@ryde/layout";
import { useCoursesNavigation } from "@ryde/redux";
import {
  BookingsViewTypeEnum,
  Log,
  selectedCoursesCalendarSlotIdsAtom,
  selectedIdsSF,
  TOOLTIP_DELAY_BUTTONS,
} from "@ryde/shared";
import { blueGreyMui, deepPurpleMui } from "@ryde/theme";

const entity = "course" as const;

export const ViewSwitchCourses: FC = () => {
  const {
    isCalendarCoursesView,
    navigateCoursesGrid,
    navigateCoursesCalendar,
  } = useCoursesNavigation();
  const setSelected = useSetRecoilState(selectedIdsSF(entity));
  const setCalendarSlotIds = useSetRecoilState(
    selectedCoursesCalendarSlotIdsAtom,
  );
  const { t } = useTranslation();

  const switchView = useEvent((viewTo: BookingsViewTypeEnum) => {
    Log.debug(`Course view switch:${BookingsViewTypeEnum[viewTo]}`);
    setSelected([]);
    setCalendarSlotIds(new Set());
    if (isCalendarCoursesView) {
      navigateCoursesGrid();
    } else {
      navigateCoursesCalendar();
    }
  });

  const colorActive = deepPurpleMui.A700;
  const colorGrey = blueGreyMui.A200;
  const iconColor = useMemo(() => {
    return {
      gridIconColor: !isCalendarCoursesView ? colorActive : colorGrey,
      calendarIconColor: isCalendarCoursesView ? colorActive : colorGrey,
    };
  }, [colorActive, colorGrey, isCalendarCoursesView]);

  const gridTooltip = t("course.calendar.switchGrid");
  const calendarTooltip = t("course.calendar.switchCalendar");

  return (
    <ViewSwitchWrapper
      data-e2e-mode={isCalendarCoursesView ? "calendar" : "grid"}
    >
      <FramerTooltip
        content={gridTooltip}
        mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
      >
        <span>
          <SVG_MUI.TableViewSVG
            fill={iconColor.gridIconColor}
            onClick={() => switchView(BookingsViewTypeEnum.Grid)}
          />
        </span>
      </FramerTooltip>
      <FramerTooltip
        content={calendarTooltip}
        mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
      >
        <span>
          <SVG_MUI.CalendarSwitch3SVG
            fill={iconColor.calendarIconColor}
            onClick={() => switchView(BookingsViewTypeEnum.Calendar)}
          />
        </span>
      </FramerTooltip>
    </ViewSwitchWrapper>
  );
};
